/**
 * ВРЕМЕННЫЙ слой доступа к API STO-210 (сотрудники / схемы оплаты / шаблоны).
 *
 * Бэкенд STO-210 ещё не задеплоен, поэтому эти методы отсутствуют в
 * `@pro-sto/api-client` (packages/api-client/src/sdk.gen.ts правится только
 * через `pnpm generate:api`, вручную — нельзя). Этот файл вручную повторяет
 * будущие operationId 1-в-1 по контракту, чтобы после деплоя и генерации
 * SDK замена была тривиальной: заменить импорт `from '@/services/employeesApi'`
 * на `from '@pro-sto/api-client'` в сторах и удалить этот файл.
 *
 * Будущие operationId, которые подменяют функции ниже:
 * adminEmployeesGetList, adminEmployeesCreate, adminEmployeesGetOne,
 * adminEmployeesUpdate, adminEmployeesDelete, adminEmployeePositionsGetList,
 * adminEmployeePaymentSchemeGet, adminEmployeePaymentSchemeUpdate,
 * adminEmployeePaymentSchemeApplyTemplate, adminEmployeePaymentSchemeUpdateService,
 * adminEmployeePaymentSchemeResetOverrides, adminPaymentSchemeTemplatesGetList,
 * adminPaymentSchemeTemplatesCreate, adminPaymentSchemeTemplatesGetOne,
 * adminPaymentSchemeTemplatesUpdate, adminPaymentSchemeTemplatesDelete,
 * adminPaymentSchemeTemplatesPropagate.
 *
 * Используется общий `axios` (default instance) — на него уже навешан
 * `setupApiInterceptors` (401 → refresh → retry), поэтому поведение
 * идентично сгенерированному клиенту. Базовый URL и токен берутся из
 * `OpenAPI` (@pro-sto/api-client), чтобы не дублировать конфигурацию.
 */
import axios, { type AxiosRequestConfig } from 'axios';
import { OpenAPI } from '@pro-sto/api-client';
import { AppError } from '@/utils/errors';
import type {
  ApplyTemplateRequest,
  ApplyTemplateResponse,
  CreateEmployeePayload,
  CreatePaymentSchemeTemplatePayload,
  Employee,
  EmployeeDetail,
  EmployeePosition,
  EmployeesListParams,
  EmployeesListResponse,
  PaymentScheme,
  PaymentSchemeTemplate,
  PaymentSchemeTemplatesListParams,
  PaymentSchemeTemplatesListResponse,
  PropagationResult,
  UpdateEmployeePayload,
  UpdatePaymentSchemePayload,
  UpdatePaymentSchemeServicePayload,
  UpdatePaymentSchemeTemplatePayload,
} from '@/types/employee.types';

const request = async <T>(config: AxiosRequestConfig): Promise<T> => {
  try {
    const response = await axios.request<T>({
      ...config,
      baseURL: OpenAPI.BASE,
      headers: {
        ...(OpenAPI.TOKEN ? { Authorization: `Bearer ${OpenAPI.TOKEN}` } : {}),
        ...config.headers,
      },
    });
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const status = error.response?.status;
      const rawMessage = (error.response?.data as { message?: string | string[] } | undefined)
        ?.message;
      // NestJS ValidationPipe (class-validator) отдаёт message массивом — по
      // одной строке на невалидное поле.
      const message = Array.isArray(rawMessage)
        ? rawMessage.join('; ')
        : rawMessage ?? 'Не удалось выполнить запрос';
      throw new AppError(message, status, status);
    }
    throw error;
  }
};

// --- Сотрудники ---

export const adminEmployeesGetList = (params: EmployeesListParams) =>
  request<EmployeesListResponse>({ method: 'GET', url: '/api/admin/employees', params });

export const adminEmployeesCreate = (data: CreateEmployeePayload) =>
  request<Employee>({ method: 'POST', url: '/api/admin/employees', data });

export const adminEmployeesGetOne = (uuid: string) =>
  request<EmployeeDetail>({ method: 'GET', url: `/api/admin/employees/${uuid}` });

export const adminEmployeesUpdate = (uuid: string, data: UpdateEmployeePayload) =>
  request<Employee>({ method: 'PATCH', url: `/api/admin/employees/${uuid}`, data });

export const adminEmployeesDelete = (uuid: string) =>
  request<{ message: string }>({ method: 'DELETE', url: `/api/admin/employees/${uuid}` });

// --- Должности ---

export const adminEmployeePositionsGetList = (serviceCenterUuid: string) =>
  request<{ data: EmployeePosition[] }>({
    method: 'GET',
    url: '/api/admin/employee-positions',
    params: { service_center_uuid: serviceCenterUuid },
  });

// --- Схема оплаты сотрудника ---

export const adminEmployeePaymentSchemeGet = (employeeUuid: string) =>
  request<PaymentScheme>({
    method: 'GET',
    url: `/api/admin/employees/${employeeUuid}/payment-scheme`,
  });

export const adminEmployeePaymentSchemeUpdate = (
  employeeUuid: string,
  data: UpdatePaymentSchemePayload
) =>
  request<PaymentScheme>({
    method: 'PATCH',
    url: `/api/admin/employees/${employeeUuid}/payment-scheme`,
    data,
  });

export const adminEmployeePaymentSchemeUpdateService = (
  employeeUuid: string,
  serviceUuid: string,
  data: UpdatePaymentSchemeServicePayload
) =>
  request<PaymentScheme>({
    method: 'PATCH',
    url: `/api/admin/employees/${employeeUuid}/payment-scheme/services/${serviceUuid}`,
    data,
  });

export const adminEmployeePaymentSchemeApplyTemplate = (
  employeeUuid: string,
  data: ApplyTemplateRequest,
  dryRun: boolean
) =>
  request<ApplyTemplateResponse>({
    method: 'POST',
    url: `/api/admin/employees/${employeeUuid}/payment-scheme/apply-template`,
    params: dryRun ? { dry_run: true } : undefined,
    data,
  });

export const adminEmployeePaymentSchemeResetOverrides = (employeeUuid: string) =>
  request<PaymentScheme>({
    method: 'DELETE',
    url: `/api/admin/employees/${employeeUuid}/payment-scheme/overrides`,
  });

// --- Шаблоны схем оплаты ---

export const adminPaymentSchemeTemplatesGetList = (params: PaymentSchemeTemplatesListParams) =>
  request<PaymentSchemeTemplatesListResponse>({
    method: 'GET',
    url: '/api/admin/payment-scheme-templates',
    params,
  });

export const adminPaymentSchemeTemplatesCreate = (data: CreatePaymentSchemeTemplatePayload) =>
  request<PaymentSchemeTemplate>({ method: 'POST', url: '/api/admin/payment-scheme-templates', data });

export const adminPaymentSchemeTemplatesGetOne = (uuid: string) =>
  request<PaymentSchemeTemplate>({ method: 'GET', url: `/api/admin/payment-scheme-templates/${uuid}` });

export const adminPaymentSchemeTemplatesUpdate = (
  uuid: string,
  data: UpdatePaymentSchemeTemplatePayload
) =>
  request<PaymentSchemeTemplate>({
    method: 'PATCH',
    url: `/api/admin/payment-scheme-templates/${uuid}`,
    data,
  });

export const adminPaymentSchemeTemplatesDelete = (uuid: string) =>
  request<{ message: string }>({
    method: 'DELETE',
    url: `/api/admin/payment-scheme-templates/${uuid}`,
  });

export const adminPaymentSchemeTemplatesPropagate = (uuid: string, dryRun: boolean) =>
  request<PropagationResult>({
    method: 'POST',
    url: `/api/admin/payment-scheme-templates/${uuid}/propagate`,
    params: dryRun ? { dry_run: true } : undefined,
  });
